import { Button, InputField, SelectField, Toggle } from '@sentinel/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SMTP_SECURITY,
  smtpSettingsUpdateSchema,
  type SmtpSettings,
  type SmtpSettingsUpdate,
} from '@sentinel/shared';

const K = 'smtp';

interface Props {
  current: SmtpSettings;
  busy: boolean;
  onSave: (update: SmtpSettingsUpdate) => void;
}

type FieldError = Partial<Record<'host' | 'port' | 'sender', string>>;

/** The fields of an outgoing mail server. The password field starts empty: left empty it keeps
 *  the stored one, which is never sent to the browser. Validated with the server's schema. */
export function EmailSettingsForm({ current, busy, onSave }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    host: current.host ?? '',
    port: String(current.port ?? 587),
    security: current.security ?? 'starttls',
    username: current.username ?? '',
    password: '',
    sender: current.sender ?? '',
    enabled: current.configured ? current.enabled : true,
  });
  const [errors, setErrors] = useState<FieldError>({});
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  const submit = () => {
    const parsed = smtpSettingsUpdateSchema.safeParse({
      ...form,
      port: Number(form.port),
      username: form.username.trim() === '' ? null : form.username,
      password: form.password === '' ? undefined : form.password,
    });
    if (parsed.success) {
      setErrors({});
      onSave(parsed.data);
      return;
    }
    const failed = new Set(parsed.error.issues.map((issue) => String(issue.path[0])));
    const next: FieldError = {};
    for (const field of ['host', 'port', 'sender'] as const)
      if (failed.has(field)) next[field] = t(`${K}.errors.${field}`);
    setErrors(next);
  };

  return (
    <div className="stack">
      <InputField
        id="smtp-host"
        label={t(`${K}.host`)}
        hint={t(`${K}.hostHint`)}
        value={form.host}
        onChange={(e) => set({ host: e.target.value })}
        error={errors.host}
      />
      <InputField
        id="smtp-port"
        label={t(`${K}.port`)}
        inputMode="numeric"
        value={form.port}
        onChange={(e) => set({ port: e.target.value })}
        error={errors.port}
      />
      <SelectField
        id="smtp-security"
        label={t(`${K}.security`)}
        value={form.security}
        onChange={(e) => set({ security: e.target.value as (typeof SMTP_SECURITY)[number] })}
        options={SMTP_SECURITY.map((s) => ({ value: s, label: t(`${K}.securities.${s}`) }))}
      />
      <InputField
        id="smtp-user"
        label={t(`${K}.username`)}
        autoComplete="off"
        value={form.username}
        onChange={(e) => set({ username: e.target.value })}
      />
      <InputField
        id="smtp-password"
        label={t(`${K}.password`)}
        type="password"
        autoComplete="new-password"
        hint={current.hasPassword ? t(`${K}.passwordSet`) : t(`${K}.passwordNone`)}
        value={form.password}
        onChange={(e) => set({ password: e.target.value })}
      />
      <InputField
        id="smtp-sender"
        label={t(`${K}.sender`)}
        hint={t(`${K}.senderHint`)}
        value={form.sender}
        onChange={(e) => set({ sender: e.target.value })}
        error={errors.sender}
      />
      <Toggle
        pressed={form.enabled}
        onToggle={(enabled) => set({ enabled })}
        label={t(`${K}.enabled`)}
      />
      <p className="note">{t(`${K}.spf`)}</p>
      <Button onClick={submit} disabled={busy} style={{ alignSelf: 'flex-start' }}>
        {t(`${K}.save`)}
      </Button>
    </div>
  );
}
